import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import Loading from '../components/Loading';
import useUser from '../hooks/useUser';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import useABTest, { trackExperimentExposure } from '../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../utils/experiments';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import {
  buildImmediateCheckoutPath,
  completePostSignupJourney,
  hasActivePostSignupJourney,
  sanitizeInternalDestination,
} from '../utils/postSignupJourney';

const PREVIEW_HOSTS = new Set(['localhost', '127.0.0.1', 'dev.botspot.trade']);

export const isPostSignupPreviewHost = (hostname: string): boolean => PREVIEW_HOSTS.has(hostname);

interface PostSignupUserIdentity {
  id?: string | number;
  howFoundUs?: string | null;
}

/**
 * Post-signup routing (Rob, 2026-10-14).
 *
 * WHY: Rob is testing one question. Do new users buy more when we charge up
 * front at checkout, or when they try the product and upgrade when they hit
 * a limit? Node decides the arm (`freeTier.enabled`, PostHog `free-tier-v1`)
 * and reports it on payment status.
 *
 * - Free tier arm: the page its landing test arm serves (Rob, 2026-10-17):
 *   a new AI Agent chat, Spot, or the dashboard. Flag off is the dashboard.
 * - Everyone else: the immediate checkout. It names no product, so the Light
 *   versus Starter checkout test keeps choosing the plan.
 *
 * The older post_signup_checkout_gate_v1 split is retired here: its control
 * arm sent new users back to a product page with no plan.
 *
 * Exceptions keep a choice the visitor already made: a paying customer, or a
 * visitor who was already headed to a checkout, a course or a challenge.
 */
const KEPT_DESTINATION_PREFIXES = ['/checkout', '/courses', '/challenges', '/challenge'];

const keepsChosenDestination = (pathname: string): boolean =>
  KEPT_DESTINATION_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

export const FREE_TIER_POST_SIGNUP_PATH = '/dashboard';

/**
 * Free tier landing test (Rob, 2026-10-17): new free tier users are split
 * three ways right after signup, so we learn which first page works best.
 * A: a new AI Agent chat. B: Spot. C: the dashboard (today's behavior).
 * Flag off, missing, unknown or failed: the dashboard.
 */
export const FREE_TIER_LANDING_FLAG = ACTIVE_EXPERIMENTS.FREE_TIER_LANDING_2026_10.featureFlag;
export const FREE_TIER_LANDING_PATHS = {
  ai_agent: '/ai-agent',
  spot: '/ai-agent?spot=open',
  dashboard: FREE_TIER_POST_SIGNUP_PATH,
} as const;
/** The landing flag's arms. Anything else the flag returns is unknown. */
const freeTierLandingVariantSchema = z.enum(['ai_agent', 'spot', 'dashboard']);
export type FreeTierLandingVariant = z.infer<typeof freeTierLandingVariantSchema>;
const FREE_TIER_LANDING_VARIANTS = freeTierLandingVariantSchema.options;

/** What PostHog (through useABTest) can return for the landing flag. */
export type FreeTierLandingFlagValue = string | boolean | null | undefined;

/**
 * Spot (`/ai-agent?spot=open`) is on main (React afcf00782, Node 545770384),
 * so the spot arm serves Spot. If Spot is ever pulled from a build, set this
 * to false: the arm then serves the dashboard and the exposure says why.
 */
export const SPOT_LANDING_AVAILABLE = true;

type FreeTierLandingFallback = null | 'flag_missing' | 'unknown_variant' | 'spot_unavailable';

export type FreeTierLanding = {
  servedVariant: FreeTierLandingVariant;
  path: string;
  fallbackReason: FreeTierLandingFallback;
};

const dashboardLanding = (fallbackReason: FreeTierLandingFallback): FreeTierLanding => ({
  servedVariant: 'dashboard',
  path: FREE_TIER_LANDING_PATHS.dashboard,
  fallbackReason,
});

export const resolveFreeTierLanding = (
  flagVariant: FreeTierLandingFlagValue,
  { spotAvailable = SPOT_LANDING_AVAILABLE }: { spotAvailable?: boolean } = {},
): FreeTierLanding => {
  if (flagVariant === undefined || flagVariant === null || flagVariant === false) {
    return dashboardLanding('flag_missing');
  }
  const parsed = freeTierLandingVariantSchema.safeParse(flagVariant);
  if (!parsed.success) return dashboardLanding('unknown_variant');
  if (parsed.data === 'spot' && !spotAvailable) return dashboardLanding('spot_unavailable');
  return {
    servedVariant: parsed.data,
    path: FREE_TIER_LANDING_PATHS[parsed.data],
    fallbackReason: null,
  };
};

/** True when the free tier user keeps a destination they already chose. */
const keepsFreeTierDestination = (destination: string): boolean => {
  const url = new URL(destination, window.location.origin);
  if (keepsChosenDestination(url.pathname)) return true;
  const isAiAgent = url.pathname === '/ai-agent' || url.pathname.startsWith('/ai-agent/');
  return isAiAgent && (url.pathname !== '/ai-agent' || url.search !== '');
};

/**
 * Where a new free tier user goes. An AI Agent link with a prompt or a saved
 * conversation is kept, so a newsletter or marketplace question still opens.
 * Everyone else lands on the page their landing test arm serves.
 */
export const buildFreeTierDestination = (
  destination: string,
  landingVariant?: FreeTierLandingFlagValue,
): string =>
  keepsFreeTierDestination(destination) ? destination : resolveFreeTierLanding(landingVariant).path;

type PostSignupRoute =
  | { kind: 'destination'; to: string }
  | { kind: 'free_tier_landing'; destination: string }
  | { kind: 'checkout'; destination: string };

export const decidePostSignupRoute = ({
  destination,
  freeTierEnabled,
  isPayingCustomer,
}: {
  destination: string;
  freeTierEnabled: boolean;
  isPayingCustomer: boolean;
}): PostSignupRoute => {
  const { pathname } = new URL(destination, window.location.origin);
  if (isPayingCustomer || keepsChosenDestination(pathname)) {
    return { kind: 'destination', to: destination };
  }
  if (freeTierEnabled) {
    return keepsFreeTierDestination(destination)
      ? { kind: 'destination', to: destination }
      : { kind: 'free_tier_landing', destination };
  }
  return { kind: 'checkout', destination };
};

function PostSignupRedirect({
  kind,
  target,
}: {
  kind: 'destination' | 'checkout';
  target: string;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    if (kind === 'checkout') {
      const backgroundUrl = new URL(target, window.location.origin);
      navigateToCheckoutOverlay(
        navigate,
        buildImmediateCheckoutPath(target),
        {
          pathname: backgroundUrl.pathname,
          search: backgroundUrl.search,
          hash: backgroundUrl.hash,
          state: null,
          key: 'post-signup-destination',
        },
        { replace: true },
      );
    } else {
      navigate(target, { replace: true });
    }
    // Keep the journey marker through the navigation commit. Landing-page
    // routing effects use it to stand down after a newly created account. If
    // it is removed before React unmounts the acquisition page, that older
    // effect can overwrite this decision and strip its parameters.
    window.setTimeout(() => completePostSignupJourney({ preserveRoutingGuard: true }), 0);
  }, [kind, navigate, target]);
  return <Loading />;
}

const freeTierLandingExperiment = ACTIVE_EXPERIMENTS.FREE_TIER_LANDING_2026_10;

/**
 * The free tier landing arm. Reads the landing flag through the shared
 * useABTest hook, records the arm actually served (a Spot fallback is the
 * dashboard), then lands the user there.
 */
function FreeTierLandingRedirect() {
  const landing = useABTest(
    freeTierLandingExperiment.name,
    freeTierLandingExperiment.featureFlag,
    'dashboard',
    {
      trackExposure: false,
      validVariants: FREE_TIER_LANDING_VARIANTS,
      surface: 'post_signup_free_tier_landing',
    },
  );
  const exposureSent = useRef(false);
  const resolved = landing.isLoading
    ? null
    : resolveFreeTierLanding(landing.isExperimentActive ? landing.variant : null);

  useEffect(() => {
    if (!resolved || exposureSent.current || !landing.isExperimentActive) return;
    exposureSent.current = true;
    trackExperimentExposure({
      experimentName: freeTierLandingExperiment.name,
      featureFlagKey: freeTierLandingExperiment.featureFlag,
      selectedVariant: resolved.servedVariant,
      exposureFlagValue: landing.flagValue ?? landing.variant,
      exposureIsActive: true,
      eventTrackingData: {
        surface: 'post_signup_free_tier_landing',
        assigned_variant: landing.variant,
        served_variant: resolved.servedVariant,
        landing_path: resolved.path,
        landing_fallback_reason: resolved.fallbackReason,
      },
    });
  }, [landing.flagValue, landing.isExperimentActive, landing.variant, resolved]);

  if (!resolved) return <Loading />;
  return <PostSignupRedirect kind="destination" target={resolved.path} />;
}

function SharedAccountViewerReturn({ destination }: { destination: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(destination, { replace: true });
    window.setTimeout(() => completePostSignupJourney({ preserveRoutingGuard: true }), 0);
  }, [destination, navigate]);
  return <Loading />;
}

export default function PostSignupDecisionPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, user, userProfile } = useUser();
  // Legacy JS context: freeTier is already normalized by PaymentStatusContext.
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
    hasResolvedPaymentStatus,
    freeTier,
  } = usePaymentStatus();
  // SAFETY: The legacy user hook supplies the authenticated BotSpot identity fields used here.
  const resolvedUserProfile = userProfile as PostSignupUserIdentity | null;
  // SAFETY: The legacy auth context supplies the same owner identifier contract.
  const authenticatedUser = user as PostSignupUserIdentity | null;
  const [authGraceExpired, setAuthGraceExpired] = useState(false);
  // Set once this page has seen a new signup's journey. Its own redirect
  // clears the journey marker; a re-render after that must not mistake the
  // user for an existing visitor and send them away from checkout.
  const sawActiveJourney = useRef(false);
  const destination = sanitizeInternalDestination(searchParams.get('redirectTo'));
  const forcedCheckoutVariant = isPostSignupPreviewHost(window.location.hostname)
    ? searchParams.get('postSignupCheckoutVariant')
    : null;
  const hasForcedPreviewDecision =
    forcedCheckoutVariant === 'test' || forcedCheckoutVariant === 'control';
  const attributionReadbackConfirmed = searchParams.get('attribution_completed') === 'true';

  useEffect(() => {
    if (isAuthenticated) {
      setAuthGraceExpired(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setAuthGraceExpired(true), 4000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated]);

  let authPending = false;
  try {
    authPending = window.sessionStorage.getItem('botspot_post_signup_auth_pending') === 'true';
  } catch {
    authPending = false;
  }

  if (isLoading || (!isAuthenticated && (!authGraceExpired || authPending))) return <Loading />;
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        replace
        to={`/login?returnTo=${encodeURIComponent(returnTo)}&source=post_signup_decision`}
      />
    );
  }

  const journeyActive = hasActivePostSignupJourney(
    resolvedUserProfile?.id || authenticatedUser?.id,
  );
  if (journeyActive) sawActiveJourney.current = true;
  if (!journeyActive && !(hasForcedPreviewDecision && attributionReadbackConfirmed)) {
    if (sawActiveJourney.current) return <Loading />;
    return <Navigate replace to={destination} />;
  }

  // The forced survey adds attribution_completed only after both the save
  // response and an authenticated profile readback confirm the persisted
  // value. Honor that handoff if this hook instance is still rendering an
  // older profile snapshot. The active journey check above keeps this marker
  // scoped to a newly created product account.
  if (!String(resolvedUserProfile?.howFoundUs || '').trim() && !attributionReadbackConfirmed) {
    const attributionParams = new URLSearchParams({
      forced_attribution: 'true',
      source_flow: 'post_signup_decision',
      redirectTo: destination,
    });
    if (forcedCheckoutVariant === 'test' || forcedCheckoutVariant === 'control') {
      attributionParams.set('postSignupCheckoutVariant', forcedCheckoutVariant);
    }
    return <Navigate replace to={`/signup-survey?${attributionParams.toString()}`} />;
  }

  // Viewing someone else's account is free, not a paid-product activation.
  // Keep attribution above, but do not enroll this read-only handoff in checkout.
  if (new URL(destination, window.location.origin).pathname.startsWith('/shared/account/')) {
    return <SharedAccountViewerReturn destination={destination} />;
  }

  // Node decides the free tier arm. Wait for payment status so a free tier
  // user never flashes through checkout. A failed status check is not proof of
  // the free tier, so it falls through to checkout.
  if (paymentLoading && !hasResolvedPaymentStatus) return <Loading />;
  const route = decidePostSignupRoute({
    destination,
    freeTierEnabled: hasResolvedPaymentStatus && freeTier?.enabled === true,
    isPayingCustomer: hasResolvedPaymentStatus && Boolean(isPayingCustomer),
  });
  if (route.kind === 'free_tier_landing') return <FreeTierLandingRedirect />;
  return (
    <PostSignupRedirect
      kind={route.kind}
      target={route.kind === 'destination' ? route.to : route.destination}
    />
  );
}
